// Fábricas de entidades novas (valores padrão). Usadas ao criar agentes/fluxos/nós.
import type {
  Agent,
  Flow,
  Guardrails,
  CostControl,
  FlowNode,
  NodeKind,
  HumanOversightMode,
} from '../types';
import { uid, nowIso } from './utils';
import { newRule } from './router';

export function defaultCostControl(): CostControl {
  return { monthlyCapBRL: 0, perRunCapBRL: 0, spentBRL: 0, onExceed: 'warn' };
}

export function defaultGuardrails(): Guardrails {
  return {
    outputFormat: 'text',
    contentFilters: {
      pii: true,
      toxicity: true,
      jailbreak: true,
      blockedTopics: [],
    },
    redactPII: false,
    moderationMode: 'heuristic',
    moderationModel: 'gpt-4.1-mini',
    allowedTools: [],
    limits: {
      maxTokens: 4096,
      maxToolCalls: 10,
      maxLoopIterations: 5,
      timeoutSec: 120,
      maxCostBRL: 5,
    },
    onViolation: 'warn',
    fallbackMessage: 'Não consigo ajudar com isso. Vou encaminhar para um humano.',
    humanApprovalActions: [],
  };
}

export function newAgent(): Agent {
  const ts = nowIso();
  return {
    id: uid('agent'),
    name: 'Novo agente',
    description: '',
    systemPrompt: 'Você é um assistente útil do Compra Agora.',
    model: 'gpt-4.1-mini',
    temperature: 0.4,
    maxTokens: 4096,
    tools: [],
    memory: 'short',
    embeddingModel: 'text-embedding-3-small',
    knowledgeBaseIds: [],
    files: [],
    artifacts: [],
    guardrails: defaultGuardrails(),
    costControl: defaultCostControl(),
    status: 'draft',
    createdAt: ts,
    updatedAt: ts,
  };
}

export function newFlow(partial?: Partial<Flow>): Flow {
  const ts = nowIso();
  const triggerId = uid('node');
  return {
    id: uid('flow'),
    name: 'Novo fluxo',
    description: '',
    status: 'draft',
    oversightMode: 'on_the_loop',
    costControl: defaultCostControl(),
    favorite: false,
    nodes: [
      {
        id: triggerId,
        kind: 'trigger',
        position: { x: 120, y: 200 },
        data: { triggerType: 'manual', samplePayload: '{\n  "mensagem": "Olá"\n}' },
      },
    ],
    edges: [],
    createdAt: ts,
    updatedAt: ts,
    ...partial,
  };
}

// Config padrão por tipo de nó (usado ao soltar na paleta).
export function defaultNodeData(kind: NodeKind): Record<string, unknown> {
  switch (kind) {
    case 'trigger':
      return { triggerType: 'manual', samplePayload: '{}' };
    case 'agent':
      return { agentId: undefined, label: 'Agente' };
    case 'llm':
      return { model: 'gpt-chat-latest', prompt: '', variables: '' };
    case 'tool':
      return { tool: 'rag', params: '{}' };
    case 'router':
      return { conditions: [newRule('Caminho A'), newRule('Caminho B')] };
    case 'loop':
      return {
        mode: 'max_iterations',
        parallel: false,
        maxIterations: 5,
        stopCondition: '',
      };
    case 'memory':
      return { operation: 'read', scope: 'short', key: '' };
    case 'kb':
      return { knowledgeBaseId: undefined, topK: 5, threshold: 0.7 };
    case 'human_review':
      return {
        message: 'Revise a saída antes de continuar.',
        slaMinutes: 30,
        fallback: 'approve',
        approvers: [],
      };
    case 'output':
      return { format: 'text', destination: 'console' };
    default:
      return {};
  }
}

export function createNode(
  kind: NodeKind,
  position: { x: number; y: number },
): FlowNode {
  return {
    id: uid('node'),
    kind,
    position,
    data: { ...defaultNodeData(kind), label: undefined },
  };
}

export const OVERSIGHT_DEFAULT: HumanOversightMode = 'on_the_loop';
